import {subMonths} from "date-fns";
import {useEffect, useRef, useState} from "react";
import ReportUtils from "../../../utils/report-utils.js";
import CloneXrayService from "../../../services/reports/clone-xray-service.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {Template} from "../../../components/template/template.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import Filters from "./components/filters.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import {Card} from "../../../components/card/card.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Content from "./components/content.jsx";

const initialFilter = ({
    dtStart: subMonths(new Date(), 1),
    dtEnd: new Date(),
});

const filtersLabels = {
    dtStart: "Data/hora inicial",
    dtEnd: "Data/hora final",
}

export default function CloneXray() {
    const [isLoading, setIsLoading] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [fixedTop, setFixedTop] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [filters, setFilters] = useState(initialFilter);
    const [headerSelected, setHeaderSelected] = useState(-1);
    const printComponentRef = useRef(null);
    const buttonRef = useRef(null);
    const title = "Raio X de notificações de clone";

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : "",
            }, filtersLabels),
        );
    }, [reportData]);

    const apply = async () => {
        ReportUtils.applyLoad({
            setLoading: setIsLoading,
            clearContent: () => {
                setReportData(null)
            }
        });

        const periodValidation = ReportUtils.periodValidate(filters.dtStart, filters.dtEnd, null, null);

        if (!periodValidation.ok) {
            ReportUtils.alertErrorMessage(periodValidation.message);
            setIsLoading(false);
            return;
        }

        try {
            const formattedFilters = {
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
            }

            const auditFilters = ReportUtils.filters({
                ...filters,
                dtStart: formattedFilters.dtStart,
                dtEnd: formattedFilters.dtEnd,
            }, filtersLabels, true);

            const response = await CloneXrayService.apply({ ...formattedFilters, filters: auditFilters });
            setReportData(response.data);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        } catch (error) {
            ReportUtils.alertErrorMessage("Erro ao gerar relatório.");
            console.error("Erro ao gerar o relatório", error);
        } finally {
            setIsLoading(false);
        }
    }

    const clearSearch = () => {
        setFilters(initialFilter);
        setReportData(null);
    }

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }
        setCollapsed(prev => !prev);
    }

    useGlobalKey("Enter", apply);

    const hasData = !!reportData && (
        (reportData?.cloneCards ?? []).some(item => Object.values(item ?? {}).some(value => Number(value ?? 0) > 0)) ||
        (reportData?.allClones ?? []).some(item => Number(item?.value ?? 0) > 0) ||
        (reportData?.notificationByPlate ?? []).some(item => Number(item?.value ?? 0) > 0) ||
        (reportData?.notificationSignByUser ?? []).some(item => Number(item?.value ?? 0) > 0) ||
        (reportData?.cloneList?.length ?? 0) > 0
    );

    const noDataValue = hasData ? 1 : 0;

    const cardClassName = [
        "w-full h-fit mr-2 print:!mt-[-15px] transform",
        !reportData || !hasData ? "mt-[59px] !bg-[#f3f3f4] !border-none !-translate-y-[5px]" : "mt-[61px]",
        collapsed ? "ml-2" : "",
    ].join(" ");

    const cardBodyClassName = [
        "flex-1",
        !reportData || !hasData ? "p-[0]" : "px-[20px] py-[5px]",
    ].join(" ");

    return (
        <div className="flex">
            <Template isLoading={isLoading}>
                <Template.Header>
                    <Template.Title>{title}</Template.Title>
                    <HeaderButtons
                        exportable={true}
                        exportInSheets={true}
                        printable={true}
                        headerChange={true}
                        printComponentRef={printComponentRef}
                        reportData={reportData}
                        data={noDataValue}
                        headerSelected={headerSelected}
                        setCollapsed={setCollapsed}
                        setHeaderSelected={setHeaderSelected}
                    />
                </Template.Header>
                <Template.Body className={`${collapsed ? "p-[0]" : ""}`}>
                    <Menu
                        buttonRef={buttonRef}
                        fixedTop={fixedTop}
                        collapsed={collapsed}
                        toggleFilters={toggleFilters}
                        clearSearch={clearSearch}
                        apply={apply}
                    >
                        <Filters filters={filters} setFilters={setFilters} />
                        <AutoDatetime setFilters={setFilters} />
                    </Menu>
                </Template.Body>
            </Template>
            <Card className={cardClassName}>
                <Card.Body className={cardBodyClassName}>
                    <NotAppliedContent reportData={reportData}  />
                    {reportData && !hasData && (
                        <NoDataContent reportData={reportData} data={noDataValue} />
                    )}
                    {reportData && hasData && (
                        <Content
                            reportData={reportData}
                            data={hasData}
                            entity={headerSelected}
                            title={title}
                            collapsed={collapsed}
                            printComponentRef={printComponentRef}
                            setIsLoading={setIsLoading}
                        />
                    )}
                </Card.Body>
            </Card>
        </div>
    )
}